'use client';

import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import DataTable from '../../../components/DataTable';
import { usePolygonTypeList, deletePolygonType } from '../../../stores/polygonTypeStore';
import ActionMenu from '../../../components/ActionMenu';
import PolygonTypeAddEdit from './PolygonTypeAddEdit';
import { fleetApi } from '../../../api/axiosClient';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { useLanguage } from '../../../utils/i18n';

function PolygonTypePageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.showAll', 'Show All') },
      { value: 'name', label: t('polygonType.polygonTypeName', 'Polygon Type Name') },
      { value: 'description', label: t('general.description', 'Description') }
    ],
    [t]
  );

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchRaw;

  const {
    data: dataFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = usePolygonTypeList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue);

  const [localData, setLocalData] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();
    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const columns = [{ logic_operator: 'like', name: 'name', operator: 'AND', value: '' }];

        let filters = [];
        if (searchRaw) {
          filters = [
            { name: 'name', value: searchRaw },
            { name: 'name', table_name: 'm_company', value: searchRaw },
            { name: 'description', value: searchRaw }
          ];
        }

        const payload = {
          paging: { start: paginationModel.page * paginationModel.pageSize, length: paginationModel.pageSize },
          orders: { columns: ['name'], ascending: true },
          joins: [{ name: 'company', column_results: ['name'] }],
          columns: columns
        };

        if (filters.length > 0) {
          payload.filters = filters;
        }

        const resp = await fleetApi.post('/polygon-type/search', payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalData(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('polygon type search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchRaw, paginationModel]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [];
      columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });

      let filters = [];
      if (searchRaw) {
        if (searchField === 'all') {
          filters = [
            { name: 'name', value: searchRaw },
            { name: 'description', value: searchRaw }
          ];
        } else {
          filters = [{ name: searchField, logic_operator: 'like', operator: 'AND', value: searchRaw }];
        }
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        columns: columns
      };

      if (searchField !== 'all' && searchRaw) {
        payload.columns = filters;
      } else if (filters.length > 0) {
        payload.filters = filters;
      }

      const resp = await fleetApi.post('/polygon-type/search', payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('polygon type export all error', err);
      return [];
    }
  };

  const rows = searchField === 'all' ? localData : dataFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal;

  const handlePageChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchValue(value);
    setSearchRaw(value);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEdit = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const handleDeleteClick = (id, itemName) => {
    if (itemName === 'fuel station' || itemName === 'power station') {
      setWarningMessage(
        <Box>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Data "<strong>{itemName}</strong>" is not allowed to be deleted because it is required for system calculations.
          </Typography>
        </Box>
      );
      setWarningOpen(true);
      return;
    }

    setDeletingId(id);
    setConfirmName(itemName);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setDeletingId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    setConfirmLoading(true);
    try {
      await deletePolygonType(deletingId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
      handleConfirmClose();
    } catch (err) {
      console.error(err);
      notify({ message: err?.response?.data?.message || t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
      setConfirmLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const columns = [
    { field: 'name', headerName: t('general.name', 'Name'), flex: 1, minWidth: 150 },
    { field: 'description', headerName: t('general.description', 'Description'), flex: 1.5, minWidth: 200 },
    {
      field: 'fill_color',
      headerName: t('polygonType.fillColor', 'Fill Color'),
      width: 120,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
          <div
            style={{
              width: 20,
              height: 20,
              backgroundColor: params.value || 'transparent',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
          <span>{params.value || '-'}</span>
        </div>
      )
    },
    {
      field: 'outline_color',
      headerName: t('polygonType.outlineColor', 'Outline Color'),
      width: 120,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
          <div
            style={{
              width: 20,
              height: 20,
              backgroundColor: params.value || 'transparent',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
          <span>{params.value || '-'}</span>
        </div>
      )
    },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      width: 80,
      sortable: false,
      align: 'center',
      renderCell: (params) => (
        <ActionMenu
          mode="inline"
          row={params.row}
          onEdit={() => handleEdit(params.id)}
          onDelete={(id) => handleDeleteClick(id, params.row.name)}
        />
      )
    }
  ];

  return (
    <div>
      <DataTable
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchValue}
        onSearch={handleSearch}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <PolygonTypeAddEdit
        open={openDialog}
        id={editingId}
        onClose={handleDialogClose}
        onSuccess={() => {
          setPaginationModel((p) => ({ ...p }));
          handleDialogClose();
        }}
      />

      <Dialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 0, overflow: 'visible', boxShadow: '0 10px 30px rgba(8,14,30,0.12)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 152, 0, 0.08))',
                border: '1px solid rgba(255,152,0,0.18)'
              }}
            >
              <WarningAmberIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <DialogTitle sx={{ p: 0, fontWeight: 700 }}>Action Denied</DialogTitle>
          </Box>
          <IconButton onClick={() => setWarningOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
          {typeof warningMessage === 'string' ? (
            <Typography variant="body1" sx={{ mb: 1.25 }}>
              {warningMessage}
            </Typography>
          ) : (
            warningMessage
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setWarningOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              fontWeight: 600,
              boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('polygonType.confirmDelete', 'Are you sure you want to delete this Polygon Type?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function PolygonTypePage() {
  return <PolygonTypePageContent />;
}
